<script lang="ts">
  /**
   * One open conversation: its messages, and the box to answer in.
   *
   * THE HOST OWNS THE MESSAGES. This renders them and does the two things a list of
   * messages needs that a data layer cannot do — keep the scroll where the reader
   * left it, and say when the top has been reached.
   *
   * That split is not a style choice. The panel already merges an optimistic overlay
   * over mirrored rows, repaints on a push socket and re-reads on a crawl cursor;
   * the web app just fetches. Owning a second copy of the list in here would mean
   * fighting the first, and the only way to reconcile them would be some counter the
   * host bumps to say "reload now" — which is a message passed through a component
   * that has no business receiving it. Messages are a prop, so a new one arriving is
   * a prop changing, and every path the host already has keeps working untouched.
   *
   * THE SCROLLER MUST BE BOUNDED or none of that works: in a container with no height
   * of its own, `flex-1` resolves to the content height, the list never overflows, and
   * `scrollTop = scrollHeight` is a no-op that leaves the reader at the OLDEST message
   * while the whole page scrolls instead. Hence `max-h`, which bites only in that
   * case — where the host does bound the height, `min-h-0 flex-1` already wins and the
   * cap is never reached. `--sg-conv-h` lets such a host say so explicitly.
   *
   * SCROLL ANCHORING is the part that is easy to get wrong. Prepending older messages
   * moves everything down by their height and throws the reader backwards mid-read.
   * The offset is measured from the BOTTOM across the load and restored after it, so
   * whatever they were reading stays under their eyes.
   */
  import { tick } from 'svelte';
  import Avatar from '../Avatar.svelte';
  import StateBlock from '../StateBlock.svelte';
  import MessageBubble from './MessageBubble.svelte';
  import type { ConversationView, DmMessageView } from './types';

  let {
    conversation,
    messages,
    /** More exist before the first of `messages`. False ends the scroll-up loader. */
    hasOlder = false,
    /** Fetch and PREPEND the page before this one. The host owns the list, so it
     *  does the prepending; this only asks, and only when the top comes into view. */
    onLoadOlder,
    loading = false,
    error = null,
    onback,
    compact = false,
    timestamp = 'relative',
    now = Date.now(),
    receipt = null,
    composer,
    threadHeader,
  }: {
    conversation: ConversationView;
    messages: DmMessageView[];
    hasOlder?: boolean;
    onLoadOlder?: (() => Promise<void>) | undefined;
    loading?: boolean;
    error?: string | null;
    onback?: (() => void) | undefined;
    /** Bubble density. Passed down from `<Conversation>`, which is already measuring
     *  the width this depends on — see `MessageBubble.compact`. */
    compact?: boolean;
    /** See `MessageBubble.timestamp`. */
    timestamp?: 'relative' | 'exact';
    now?: number;
    /**
     * Replaces the default identity header.
     *
     * The default names who you are talking to, which is the whole question in a
     * panel. It is not the question in the superadmin explorer, where the reader
     * already picked the inbox and needs the channel's id, its backfill state and its
     * watermark — so that surface supplies its own strip rather than gaining a row of
     * forensic chips nobody else wants under a header nobody there is reading.
     */
    threadHeader?: import('svelte').Snippet | undefined;
    /**
     * Whether the counterpart has caught up with what we sent them.
     *
     * PER CONVERSATION. Skool's only read signal is `unread_user_ids` on
     * `/self/chat-channels` — the users a conversation is unread FOR — so it says
     * "they are up to date", not "they opened this one". `receiptFrom` below is what
     * turns that into ticks a reader can trust against particular bubbles.
     *
     * There is also NO READ TIME to show: `metadata.last_read`/`last_read_at` look like
     * one and are the viewer's own watermark (on every row they equal that channel's
     * last message). So this is a pair of ticks, and never "read 2h ago".
     *
     * Null renders nothing, which is the honest state for a surface that has not been
     * told: a conversation older than the column, or one Skool did not report.
     */
    receipt?: 'sent' | 'read' | null;
    /** The reply box, supplied by the host so it is that app's shared composer
     *  rather than a second one grown in here. */
    composer?: import('svelte').Snippet | undefined;
  } = $props();

  /**
   * The first message the receipt is allowed to speak for — the start of the run we
   * have sent since they last said anything. `-1` when there is no such run.
   *
   * WHY A RUN AND NOT THE WHOLE THREAD. Anything of ours that comes BEFORE one of
   * their messages was obviously read: they answered it. Anything after is what the
   * conversation-level flag is actually about, so those are the bubbles that carry
   * ticks — which is also where a messenger puts them.
   *
   * It under-claims by design at one edge: if they opened the thread part-way through
   * the run and we have sent more since, the earlier ones show as not-read. Saying
   * "read" of a message nobody has opened is the mistake worth avoiding, so the run
   * moves together and only ever errs quiet.
   */
  const receiptFrom = $derived.by(() => {
    if (!receipt) return -1;
    let i = messages.length;
    while (i > 0 && messages[i - 1]?.outbound === true) i--;
    return i === messages.length ? -1 : i;
  });

  let scroller = $state<HTMLElement | null>(null);
  let topSentinel = $state<HTMLElement | null>(null);
  let loadingOlder = $state(false);

  /** Follow new messages down — but only when the reader is already at the bottom.
   *  Yanking someone to the newest message while they are reading history is the
   *  other half of the anchoring problem. */
  let atBottom = $state(true);
  function onScroll(): void {
    const el = scroller;
    if (!el) return;
    atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  /**
   * Which conversation has already been anchored, so opening one is told apart from
   * receiving a message in the one already open. A plain variable, not `$state`:
   * nothing renders from it and the effect below must not re-run when it is set.
   */
  let anchoredFor: string | null = null;

  $effect(() => {
    const el = scroller;
    const id = conversation.id;
    const count = messages.length;
    if (!el || count === 0) return;
    // OPENING ALWAYS ANCHORS, `atBottom` or not. It is only a question for a thread
    // already on screen — and trusting it here is what broke the first open of a
    // session: the browser fires a scroll event as the list lays out, `onScroll`
    // reads a scrollTop of 0 against a tall thread, concludes the reader is not at
    // the bottom, and the anchor never runs. The reader had not scrolled at all.
    if (anchoredFor !== id && !atBottom) atBottom = true;
    if (!atBottom) return;
    void tick().then(() => {
      const s = scroller;
      if (!s) return;
      s.scrollTop = s.scrollHeight;
      atBottom = true;
      anchoredFor = id;
    });
  });

  /** Stay pinned to the newest message across a RESIZE. The owner drags the side
   *  panel wider and every bubble reflows to a different height; without this the
   *  reader who was at the bottom is left somewhere in the middle of the thread. */
  $effect(() => {
    const el = scroller;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (atBottom) el.scrollTop = el.scrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  });

  async function loadOlder(): Promise<void> {
    const el = scroller;
    if (loadingOlder || !hasOlder || !onLoadOlder || !el) return;
    loadingOlder = true;
    // Distance from the BOTTOM survives a prepend; `scrollTop` does not.
    const fromBottom = el.scrollHeight - el.scrollTop;
    try {
      await onLoadOlder();
      await tick();
      el.scrollTop = el.scrollHeight - fromBottom;
    } finally {
      loadingOlder = false;
    }
  }

  /** Fires when the top comes into view, rather than on every scroll event. */
  $effect(() => {
    const el = topSentinel;
    const root = scroller;
    if (!el || !root || !hasOlder) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadOlder();
      },
      { root, rootMargin: '120px 0px 0px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  });
</script>

<div class="flex h-full min-h-0 flex-col gap-2">
  <div class="flex items-center gap-2 rounded-box border hair bg-base-100 p-2">
    {#if onback}
      <button class="btn btn-ghost btn-xs px-1" onclick={onback} aria-label="Back to conversations">←</button>
    {/if}
    {#if threadHeader}
      {@render threadHeader()}
    {:else}
      <Avatar src={conversation.pictureUrl} name={conversation.name} size={28} memberId={conversation.memberId ?? null} />
      <span class="min-w-0 flex-1">
        <span class="redact block truncate text-xs font-semibold">{conversation.name}</span>
        {#if conversation.handle}
          <span class="redact block truncate font-mono text-[10px] opacity-55">@{conversation.handle}</span>
        {/if}
      </span>
    {/if}
  </div>

  {#if loading && messages.length === 0}
    <StateBlock state="loading" message="Loading messages…" compact />
  {:else if error}
    <StateBlock state="error" message={error} compact />
  {:else if messages.length === 0}
    <StateBlock state="empty" message="No messages mirrored yet." hint="The Helper syncs on its next pass." compact />
  {:else}
    <div
      bind:this={scroller}
      onscroll={onScroll}
      class="min-h-0 max-h-[var(--sg-conv-h,70vh)] flex-1 space-y-1.5 overflow-y-auto rounded-box border hair bg-base-100 p-2.5"
    >
      <div bind:this={topSentinel} aria-hidden="true"></div>
      {#if loadingOlder}
        <p class="py-1 text-center text-[10px] opacity-55">Loading earlier messages…</p>
      {:else if !hasOlder}
        <!-- Answers "is there more?", which an infinite scroller otherwise never does. -->
        <p class="py-1 text-center text-[10px] opacity-40">Beginning of the conversation</p>
      {/if}
      {#each messages as m, i (m.id)}
        <MessageBubble
          message={m}
          receipt={receiptFrom >= 0 && i >= receiptFrom ? receipt : null}
          owner={conversation.owner ?? null}
          counterpart={{
            name: conversation.name,
            pictureUrl: conversation.pictureUrl,
            memberId: conversation.memberId ?? null,
          }}
          skoolUrl={conversation.skoolUrl ?? null}
          {compact}
          {timestamp}
          {now}
        />
      {/each}
    </div>
  {/if}

  {#if composer}{@render composer()}{/if}
</div>
